import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import {
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { Box, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
} from 'generated/graphql';
import { ChangeEvent, useState } from 'react';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

export const StellaConnectFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
  integrationID,
}: GetAddIntegrationFormDrawerContentProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');

  const { status: createIntegrationStatus, createIntegration } =
    useCreateIntegrationEdgeServer({
      tenantID,
      direction: IntegrationDirection.Inbound,
      channel: IntegrationChannelType.StellaConnect,
      integrationType,
      createdByUser: userID,
      inputData: {
        stellaConnect: {
          apiKey,
          secretKey,
        },
      },
      description: descriptionInput.trim(),
      onCompleted: () => {
        onDrawerClose();
        trackIntegrationEvent({
          eventName: 'Integration Added',
          integrationChannelType: IntegrationChannelType.StellaConnect,
        });
      },
    });

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      action: 'reauth',
      channelType: IntegrationChannelType.StellaConnect,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
      },
    });

  const isReauthorizeForm = !!integrationID;

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    shouldValidateDescription: !isReauthorizeForm,
    channelType: IntegrationChannelType.StellaConnect,
    integrationType,
    hasValidFormFields: isReauthorizeForm
      ? !!secretKey
      : !!apiKey && !!secretKey,
    descriptionInput,
    currentDescription: '',
    onValidated: async () => {
      if (isReauthorizeForm) {
        await updateIntegration({
          integrationID,
          isActive: true,
          updateAuthData: { stellaConnect: { secretKey } },
        });
      } else {
        await createIntegration();
        trackIntegrationEvent({
          eventName: 'Integration Form Connect CTA Clicked',
          integrationChannelType: IntegrationChannelType.StellaConnect,
        });
      }
    },
  });

  const handleAPIKeyInputField = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setApiKey(event.target.value);
  };

  const handleSecretKeyInputField = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSecretKey(event.target.value);
  };

  const isLoading =
    IsLoading(createIntegrationStatus) || IsLoading(updateIntegrationStatus);

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
          isReauthorizeForm={isReauthorizeForm}
        />
        <Stack p={3} spacing={4}>
          <Stack spacing={2}>
            {!isReauthorizeForm && (
              <TextField
                required
                fullWidth
                label="API Key"
                placeholder="Enter API Key"
                variant="outlined"
                onChange={handleAPIKeyInputField}
                disabled={isLoading}
              />
            )}
            <TextField
              required
              fullWidth
              label="Secret Key"
              placeholder="Enter Secret Key"
              variant="outlined"
              onChange={handleSecretKeyInputField}
              disabled={isLoading}
            />
          </Stack>
          {!isReauthorizeForm && (
            <IntegrationDescriptionForm
              value={descriptionInput}
              onChange={setDescriptionInput}
              isRequired={isDescriptionRequired}
              validatedDescriptionMessage={validatedDescriptionMessage}
              isValidationInProgress={isValidatingDescription}
              descriptionValidForQuestions={descriptionValidForQuestions}
              isPreValidatedDescription={isPreValidatedDescription}
              integrationType={integrationType}
            />
          )}
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        buttonText={isReauthorizeForm ? 'Reauthorize' : 'Integrate'}
        isDisabled={isDisabled}
        isLoading={isLoading}
      />
    </Stack>
  );
};
